//
// Copyright (c) 2025 Comcast
//

import { useState } from 'react'
import PropTypes from 'prop-types'
import AsyncSelect from 'react-select/async'
import { useTranslation } from 'react-i18next'

import {
  StyledSelectWrapper, getCustomStyles,
} from 'components/Shared/Select/utils'
import SelectControl from 'components/Shared/Select/components/SelectControl'
import Menu from 'components/Shared/Select/components/Menu'
import ClearIndicator from 'components/Shared/Select/components/ClearIndicator'
import DropdownIndicator from 'components/Shared/Select/components/DropdownIndicator'
import LoadingIndicator from 'components/Shared/Select/components/LoadingIndicator'
import MultiValueRemove from 'components/Shared/Select/components/MultiValueRemove'
import NoOptionsMessage from 'components/Shared/Select/components/NoOptionsMessage'
import LoadingMessage from 'components/Shared/Select/components/LoadingMessage'
import Input from 'components/Shared/Select/components/Input'
import Option from 'components/Shared/Select/components/Option'
import MultiValueLabel from './components/MultiValueLabel'
import MultiValueContainer from './components/MultiValueContainer'

// load options for queries starting from this length
const MIN_QUERY_LENGTH = 3

const SelectAsync = ({
  value, onChange, disabled, multiselect, usePortal, portalZindex,
  className, onLoadOptions, placeholder, isLoading,
}) => {
  const [inputValue, setInputValue] = useState('')
  const { t } = useTranslation()

  const handleLoadOptions = (inputValue, cbFunc) => {
    if (inputValue.length < MIN_QUERY_LENGTH) {
      setTimeout(() => cbFunc([]), 1)
      return
    }

    onLoadOptions(inputValue, cbFunc)
  }

  const handleInputChange = (newInputValue, { action }) => {
    if (action !== 'set-value') {
      setInputValue(newInputValue)
      return newInputValue
    }
    return inputValue
  }

  const handleOnChange = (item) => {
    let newValue = item
    if (multiselect && !item) {
      newValue = []
    }

    onChange(newValue)
  }

  const components = {
    IndicatorSeparator: () => null,
    Control: SelectControl,
    Menu: Menu,
    DropdownIndicator: DropdownIndicator,
    LoadingIndicator: LoadingIndicator,
    ClearIndicator: ClearIndicator,
    MultiValueContainer: MultiValueContainer,
    MultiValueLabel: MultiValueLabel,
    MultiValueRemove: MultiValueRemove,
    NoOptionsMessage: NoOptionsMessage,
    LoadingMessage: LoadingMessage,
    Input: Input,
    Option: Option,
  }

  return (
    <StyledSelectWrapper className={className} disabled={disabled}>
      <AsyncSelect
        isMulti={multiselect}
        closeMenuOnSelect={!multiselect}
        hideSelectedOptions={false}
        value={value}
        inputValue={inputValue}
        onChange={handleOnChange}
        loadOptions={handleLoadOptions}
        cacheOptions={inputValue}
        onInputChange={handleInputChange}
        components={components}
        styles={getCustomStyles({ portalZindex })}
        menuPortalTarget={usePortal ? document.body : undefined}
        isDisabled={disabled}
        defaultMenuIsOpen={false} /* use it for debugging */
        classNamePrefix='react-select'
        noOptionsMessage={() => t('select.noOptions')}
        loadingMessage={() => t('select.loadingMessage')}
        placeholder={placeholder || t('select.placeholder')}
        isLoading={isLoading}
      />
    </StyledSelectWrapper>
  )
}

SelectAsync.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  multiselect: PropTypes.bool,
  usePortal: PropTypes.bool,
  portalZindex: PropTypes.number,
  onLoadOptions: PropTypes.func,
  placeholder: PropTypes.string,
  isLoading: PropTypes.bool,
}

export default SelectAsync
